import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Sparkles, RotateCw } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import insightService from '../services/insightService';
import DisplayHeading from '../components/primitives/DisplayHeading';
import HairlineDivider from '../components/primitives/HairlineDivider';
import MonoCaption from '../components/primitives/MonoCaption';

const COLOR_MAP = {
  starAmber: '#F4B860',
  starBlue: '#6B8CCB',
  starViolet: '#9C7BC9',
  starRose: '#D08A8A',
  starMint: '#7FB89A',
  starGold: '#E6C77E',
};

/* =====================================================
 * Layout
 * ===================================================== */
const Page = styled.div`
  position: relative;
  min-height: 100vh;
  background: ${p => p.theme.colors.bgPrimary};
  color: ${p => p.theme.colors.text};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 40% at 30% 20%, rgba(156,123,201,0.06), transparent 60%),
      radial-gradient(ellipse 50% 35% at 75% 80%, rgba(107,140,203,0.05), transparent 65%);
    pointer-events: none;
    z-index: 0;
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 1;
  max-width: ${p => p.theme.containers.narrow};
  margin: 0 auto;
  padding: 64px 24px 96px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    padding: 40px 16px 64px;
  }
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: ${p => p.theme.colors.textMuted};
  font-family: ${p => p.theme.fonts.mono};
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 8px 4px 8px 0;
  margin-bottom: 28px;
  transition: color 0.2s;
  &:hover { color: ${p => p.theme.colors.starGold}; }
`;

/* ===== Topic cards ===== */
const TopicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-top: 40px;

  @media (max-width: ${p => p.theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const TopicCard = styled(motion.button)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 22px;
  background: ${p => p.theme.colors.gradCard};
  border: 1px solid ${p => p.theme.colors.lineFaint};
  border-top: 2px solid ${p => p.$color};
  border-radius: ${p => p.theme.borderRadius.md};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: transform 0.25s ease, border-color 0.25s ease;
  min-height: 140px;

  &:hover {
    transform: translateY(-2px);
    border-color: ${p => p.theme.colors.lineSoft};
    border-top-color: ${p => p.$color};
  }
`;

const TopicIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${p => p.theme.colors.bgPrimary};
  color: ${p => p.$color};
  font-size: 1.1rem;
  box-shadow: 0 0 14px ${p => p.$color}33;
`;

const TopicName = styled.h3`
  margin: 8px 0 0;
  font-family: ${p => p.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: 1.2rem;
  color: ${p => p.theme.colors.text};
`;

const TopicDesc = styled.p`
  margin: 4px 0 0;
  font-size: 0.85rem;
  line-height: 1.7;
  color: ${p => p.theme.colors.textSecondary};
`;

/* ===== Result panel ===== */
const ResultPanel = styled(motion.div)`
  margin-top: 32px;
  padding: 32px 28px;
  background: ${p => p.theme.colors.gradCard};
  border: 1px solid ${p => p.theme.colors.lineFaint};
  border-top: 2px solid ${p => p.$color};
  border-radius: ${p => p.theme.borderRadius.md};
`;

const ResultHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 18px;
`;

const ResultTitle = styled.h2`
  margin: 0;
  font-family: ${p => p.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: 1.35rem;
  color: ${p => p.theme.colors.text};
`;

const ResultBody = styled.div`
  font-size: 0.96rem;
  line-height: 2;
  color: ${p => p.theme.colors.textSecondary};
  white-space: pre-wrap;
`;

const ResultActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
  flex-wrap: wrap;
`;

const SmallBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  background: transparent;
  color: ${p => p.theme.colors.textSecondary};
  border: 1px solid ${p => p.theme.colors.lineSoft};
  border-radius: 9999px;
  font-size: 0.82rem;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
  &:hover {
    color: ${p => p.theme.colors.starGold};
    border-color: ${p => p.theme.colors.lineGold};
  }
`;

const LoginNotice = styled.div`
  margin-top: 24px;
  padding: 18px 22px;
  background: ${p => p.theme.colors.bgPrimary};
  border: 1px solid ${p => p.theme.colors.lineFaint};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: 0.88rem;
  color: ${p => p.theme.colors.textMuted};
  line-height: 1.85;
`;

/* =====================================================
 * Component
 * ===================================================== */
const InsightPage = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { isAuthenticated } = useAuth();

  const [topics, setTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [activeTopic, setActiveTopic] = useState(null);
  const [result, setResult] = useState(null);   // { topic, text, model }
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [context, setContext] = useState(null); // 認証済みなら /api/v3/context から

  useEffect(() => {
    let alive = true;
    insightService.listTopics()
      .then(d => { if (alive) setTopics(d.topics || []); })
      .catch(e => { if (alive) setError(String(e)); })
      .finally(() => { if (alive) setLoadingTopics(false); });
    if (isAuthenticated) {
      insightService.getContext()
        .then(d => { if (alive) setContext(d); })
        .catch(() => { /* 未診断などで失敗するのは想定内 */ });
    }
    return () => { alive = false; };
  }, [isAuthenticated]);

  const handleGenerate = async (topicKey) => {
    setError(null);
    setActiveTopic(topicKey);
    setGenerating(true);
    setResult(null);
    try {
      const ctx = context ? {
        type_code: context.latest_current?.type_code || context.latest_natal?.type_code,
        axes: context.latest_current?.axes || context.latest_natal?.axes,
        user_memo: context.ai_context_level === 'full' ? context.self_memo : null,
        birth_date: context.ai_context_level === 'full' ? context.profile_extended?.birth_date : null,
        birth_place: context.ai_context_level === 'full' ? context.profile_extended?.birth_place : null,
      } : null;
      const res = await insightService.generate({
        topic: topicKey,
        context: ctx,
        additional_inputs: {},
      });
      setResult(res);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setGenerating(false);
    }
  };

  const activeDef = useMemo(
    () => topics.find(tp => tp.key === activeTopic),
    [topics, activeTopic],
  );

  return (
    <Page>
      <Container>
        <BackLink onClick={() => navigate('/')}>
          <ArrowLeft size={14} />
          {t('ホームへ', 'Home')}
        </BackLink>

        <DisplayHeading
          eyebrow={t('テーマ別の問い', 'By Topic')}
          title={t('テーマを選んで、自分を読みなおす。', 'Pick a topic, re-read yourself.')}
          subtitle={t(
            'あなたのコードと軸スコアから、テーマごとに短い読み解きを返します。',
            'A short reading per topic, drawn from your code and axis scores.'
          )}
          align="left"
          italic
        />

        {!isAuthenticated && (
          <LoginNotice>
            {t(
              '読み解きの生成にはログインが必要です。',
              'Please sign in to generate a reading.'
            )}
          </LoginNotice>
        )}

        {loadingTopics ? (
          <LoginNotice>{t('テーマを読み込んでいます…', 'Loading topics…')}</LoginNotice>
        ) : (
          <TopicGrid>
            {topics.map((tp, i) => {
              const color = COLOR_MAP[tp.color_token] || COLOR_MAP.starGold;
              const isActive = tp.key === activeTopic;
              return (
                <TopicCard
                  key={tp.key}
                  $color={color}
                  onClick={() => handleGenerate(tp.key)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                  disabled={generating && isActive}
                >
                  <TopicIcon $color={color}>{tp.icon}</TopicIcon>
                  <TopicName>{lang === 'en' ? tp.label_en : tp.label_ja}</TopicName>
                  <TopicDesc>{lang === 'en' ? (tp.description_en || tp.description_ja) : tp.description_ja}</TopicDesc>
                </TopicCard>
              );
            })}
          </TopicGrid>
        )}

        {error && (
          <LoginNotice style={{ borderColor: 'rgba(239,68,68,0.4)', color: '#f87171' }}>
            {error}
          </LoginNotice>
        )}

        {(generating || result) && (
          <ResultPanel
            $color={COLOR_MAP[activeDef?.color_token] || COLOR_MAP.starGold}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <ResultHead>
              <MonoCaption upper gold>
                {activeDef ? (lang === 'en' ? activeDef.label_en : activeDef.label_ja) : ''}
              </MonoCaption>
              {generating && (
                <MonoCaption upper>
                  {t('読み解いています…', 'Reading…')}
                </MonoCaption>
              )}
            </ResultHead>
            <ResultTitle>
              {generating
                ? t('しばらくお待ちください。', 'A moment, please.')
                : t('読み解きが届きました。', 'Your reading.')}
            </ResultTitle>
            <div style={{ height: 16 }} />
            <ResultBody>{generating ? '' : result?.text}</ResultBody>
            {result && (
              <ResultActions>
                <SmallBtn onClick={() => handleGenerate(result.topic)}>
                  <RotateCw size={14} />
                  {t('もう一度読み解く', 'Re-read')}
                </SmallBtn>
                <SmallBtn onClick={() => navigate('/chat')}>
                  <Sparkles size={14} />
                  {t('チャットで掘り下げる', 'Continue in chat')}
                  <ArrowRight size={12} />
                </SmallBtn>
              </ResultActions>
            )}
          </ResultPanel>
        )}

        <HairlineDivider mark="✦" margin="56px 0 0" />
      </Container>
    </Page>
  );
};

export default InsightPage;
